import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Truck } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES } from "@/data/site";

const FREIGHT_TYPES = [...SERVICES.map((s) => s.title), "Other / Not Sure"];

const empty = { name: "", company: "", email: "", phone: "", origin: "", destination: "", freight_type: "", weight: "", dimensions: "", ready_date: "", details: "" };

const Field = ({ label, k, type = "text", required, placeholder, form, set }) => (
  <div>
    <Label className="font-head uppercase tracking-wide text-xs font-700 text-[#0B192C]">{label}{required && <span className="text-[#FF5A00]"> *</span>}</Label>
    <Input type={type} value={form[k]} onChange={set(k)} placeholder={placeholder} data-testid={`quote-${k}`}
      className="mt-2 rounded-none border-2 border-[#0B192C]/15 focus-visible:ring-0 focus-visible:border-[#FF5A00] h-12" />
  </div>
);

export default function Quote() {
  useSEO("Get a Free Quote", "Request a free freight quote from Cargowyn. Tell us your origin, destination, freight type and dimensions for an honest, all-in cross-border rate.");
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.origin || !form.destination || !form.freight_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] py-24" data-testid="quote-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-lg px-5">
          <div className="w-20 h-20 bg-[#FF5A00] flex items-center justify-center mx-auto mb-8"><CheckCircle2 size={40} className="text-white" /></div>
          <h1 className="font-head font-black uppercase text-5xl tracking-tighter">Request received</h1>
          <p className="text-[#475569] text-lg mt-4">Thanks, {form.name.split(" ")[0]}. Our team will review your shipment and reply with an honest, all-in rate — usually within one business day.</p>
          <button onClick={() => { setForm(empty); setDone(false); }} data-testid="quote-another" className="mt-8 border-2 border-[#0B192C] font-head font-700 uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#0B192C] hover:text-white transition-colors">Submit Another</button>
        </motion.div>
      </section>
    );
  }

  return (
    <div>
      <section className="bg-[#0B192C] text-white pt-24 pb-20 relative overflow-hidden" data-testid="quote-hero">
        <div className="absolute inset-0 noise opacity-[0.05]" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <div className="flex items-center gap-3 mb-5"><Truck className="text-[#FF5A00]" /><span className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">Free Quote</span></div>
          <h1 className="font-head font-black uppercase text-6xl md:text-8xl tracking-tighter leading-[0.85] max-w-4xl">Tell us what you're moving</h1>
          <p className="text-[#94A3B8] text-lg mt-6 max-w-xl">No obligations, no hidden fees. Share the details below and we'll come back with a clear, all-in rate.</p>
        </div>
      </section>

      <section className="py-20 bg-[#F8FAFC]">
        <form onSubmit={submit} className="max-w-4xl mx-auto px-5 lg:px-8" data-testid="quote-form">
          <div className="bg-white border-2 border-[#0B192C] p-8 lg:p-12 space-y-10">
            <div>
              <h2 className="font-head font-800 uppercase text-2xl mb-6 pb-3 border-b-2 border-[#FF5A00] inline-block">1 · Contact</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Full Name" k="name" required placeholder="Jane Doe" form={form} set={set} />
                <Field label="Company" k="company" placeholder="Acme Manufacturing" form={form} set={set} />
                <Field label="Email" k="email" type="email" required placeholder="jane@company.com" form={form} set={set} />
                <Field label="Phone" k="phone" required placeholder="(647) 555-0000" form={form} set={set} />
              </div>
            </div>

            <div>
              <h2 className="font-head font-800 uppercase text-2xl mb-6 pb-3 border-b-2 border-[#FF5A00] inline-block">2 · Shipment</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Origin" k="origin" required placeholder="Toronto, ON" form={form} set={set} />
                <Field label="Destination" k="destination" required placeholder="Chicago, IL" form={form} set={set} />
                <div>
                  <Label className="font-head uppercase tracking-wide text-xs font-700 text-[#0B192C]">Freight Type<span className="text-[#FF5A00]"> *</span></Label>
                  <Select value={form.freight_type} onValueChange={set("freight_type")}>
                    <SelectTrigger className="mt-2 rounded-none border-2 border-[#0B192C]/15 h-12 focus:ring-0" data-testid="quote-freight_type"><SelectValue placeholder="Select freight type" /></SelectTrigger>
                    <SelectContent className="rounded-none border-2 border-[#0B192C]">
                      {FREIGHT_TYPES.map((t) => <SelectItem key={t} value={t} className="rounded-none">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Ready Date" k="ready_date" type="date" form={form} set={set} />
                <Field label="Weight" k="weight" placeholder="e.g. 22,000 lbs" form={form} set={set} />
                <Field label="Dimensions" k="dimensions" placeholder="e.g. 48ft x 8.5ft x 9ft" form={form} set={set} />
              </div>
            </div>

            <div>
              <h2 className="font-head font-800 uppercase text-2xl mb-6 pb-3 border-b-2 border-[#FF5A00] inline-block">3 · Details</h2>
              <Label className="font-head uppercase tracking-wide text-xs font-700 text-[#0B192C]">Anything else we should know?</Label>
              <Textarea value={form.details} onChange={set("details")} rows={4} data-testid="quote-details" placeholder="Permits required, appointment times, special handling, etc." className="mt-2 rounded-none border-2 border-[#0B192C]/15 focus-visible:ring-0 focus-visible:border-[#FF5A00]" />
            </div>

            <button type="submit" disabled={loading} data-testid="quote-submit-button" className="w-full bg-[#FF5A00] text-white font-head font-700 uppercase tracking-[0.2em] py-5 flex items-center justify-center gap-3 hover:bg-[#E65100] transition-colors disabled:opacity-60">
              {loading ? "Sending..." : <>Request My Quote <ArrowRight size={20} /></>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
